import { useCallback, useMemo, useState } from "react"
import {
  useAddApprovedUrlMutation,
  useDeclineEmbedDomainMutation,
  useGetPendingEmbedDomainsQuery
} from "../../redux/queries"
import { selectUserDetails } from "../../redux/slices/auth"
import { DeclinedEmbedDomain, PendingEmbedDomain } from "../../types"
import { useAppSelector } from "./use-store"
import { useToast } from "./use-toast"

/** How far back to look for blocked embed requests. */
const LOOKBACK_DAYS = 30
// Well above any realistic number of blocked domains, so the Status tab can
// filter to one integration client-side without a truncated list hiding a row.
// Kept constant across callers so they all share one cached request.
const LIST_LIMIT = 100
const STORAGE_KEY_PREFIX = "bb:blocked-embed-domains-dismissed"

const storageKey = (orgId: string) => `${STORAGE_KEY_PREFIX}:${orgId}`

// localStorage throws in private-browsing / storage-disabled contexts, and a
// notification is never worth breaking a page over.
const readDismissed = (orgId: string): string[] => {
  if (!orgId) return []
  try {
    const raw = window.localStorage.getItem(storageKey(orgId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.filter(h => typeof h === "string")
      : []
  } catch {
    return []
  }
}

const writeDismissed = (orgId: string, hosts: string[]) => {
  if (!orgId) return
  try {
    window.localStorage.setItem(storageKey(orgId), JSON.stringify(hosts))
  } catch {
    // ignore — the banner just reappears next session
  }
}

type UsePendingEmbedDomainsOptions = {
  enabled?: boolean
  /** Narrow to domains blocked on one integration (the Status tab view). */
  integrationId?: string
}

type UsePendingEmbedDomainsReturn = {
  /** Blocked domains as returned by the API (most recent first). */
  domains: PendingEmbedDomain[]
  /**
   * How many domains are blocked, ignoring dismissal. Org-wide unless
   * `integrationId` narrowed the list.
   */
  count: number
  /** Blocked domains the user has not dismissed the banner for yet. */
  undismissed: PendingEmbedDomain[]
  /** Dismiss the banner for every domain currently blocked. */
  dismiss: () => void
  /** Approve a domain by saving the pattern the API suggested for it. */
  approve: (domain: PendingEmbedDomain) => Promise<boolean>
  /** Host currently being approved, for a per-row spinner. */
  approvingHost: string | null
  /**
   * Decline a domain: it stays blocked, it just stops being reported. Unlike
   * `dismiss` this is saved server-side, so it holds for every user in the org
   * and clears the nav badge too.
   */
  decline: (domain: PendingEmbedDomain) => Promise<boolean>
  /** Host currently being declined, for a per-row spinner. */
  decliningHost: string | null
  /** Previously declined domains, so a decline can be undone. */
  declined: DeclinedEmbedDomain[]
  /** Undo a decline, putting the domain back in the pending list. */
  restore: (domain: DeclinedEmbedDomain) => Promise<boolean>
  /** Host currently being restored, for a per-row spinner. */
  restoringHost: string | null
  isFetching: boolean
}

/**
 * Domains that tried to load one of the org's embeds and were blocked for not
 * being on the approved-URL list — the signal behind the Integrations nav
 * badge, the dashboard banner and the Status tab card.
 *
 * Two different ways to make a row go away, and they are not the same thing:
 *
 * - `dismiss` is "not now": per-browser localStorage, banner only. `count`
 *   (the nav badge) ignores it, and a new blocked domain brings the banner
 *   back. Nothing is recorded server-side.
 * - `decline` is a decision: saved server-side for the whole org, so the row
 *   leaves the banner, the Status tab card AND the nav badge, for every user
 *   and every browser. `restore` undoes it.
 *
 * Neither unblocks anything — only `approve` changes what the embed will load.
 */
export const usePendingEmbedDomains = (
  options: UsePendingEmbedDomainsOptions = {}
): UsePendingEmbedDomainsReturn => {
  const { enabled = true, integrationId } = options

  const user = useAppSelector(selectUserDetails)
  const orgId = user?.org?.id ?? ""
  const { pushSuccessToast, pushErrorToast } = useToast()

  const { data, isFetching, refetch } = useGetPendingEmbedDomainsQuery(
    { org_id: orgId, days: LOOKBACK_DAYS, limit: LIST_LIMIT },
    { skip: !enabled || !orgId }
  )

  const [addApprovedUrl] = useAddApprovedUrlMutation()
  const [declineEmbedDomain] = useDeclineEmbedDomainMutation()
  const [approvingHost, setApprovingHost] = useState<string | null>(null)
  const [decliningHost, setDecliningHost] = useState<string | null>(null)
  const [restoringHost, setRestoringHost] = useState<string | null>(null)
  // Hosts approved or declined in this session. The API stops returning them
  // as pending once the refetch lands, but dropping them locally means the row
  // disappears on click instead of after the round trip.
  const [resolvedHosts, setResolvedHosts] = useState<string[]>([])
  // Hosts un-declined in this session, dropped from the undo list for the same
  // reason — they are on their way back to the pending list.
  const [restoredHosts, setRestoredHosts] = useState<string[]>([])

  const [dismissedHosts, setDismissedHosts] = useState<string[]>(() =>
    readDismissed(orgId)
  )

  const domains = useMemo(() => {
    const all = data?.pending_domains ?? []
    const live = all.filter(({ host }) => !resolvedHosts.includes(host))
    if (!integrationId) return live
    return live.filter(({ integrations }) =>
      integrations.some(({ id }) => String(id) === String(integrationId))
    )
  }, [data, resolvedHosts, integrationId])

  // Declined domains have no integration list to narrow by — a decline is
  // org-wide, so the same undo list is correct on every Status tab.
  const declined = useMemo(
    () =>
      (data?.declined_domains ?? []).filter(
        ({ host }) => !restoredHosts.includes(host)
      ),
    [data, restoredHosts]
  )

  const undismissed = useMemo(
    () => domains.filter(({ host }) => !dismissedHosts.includes(host)),
    [domains, dismissedHosts]
  )

  const dismiss = useCallback(() => {
    const hosts = Array.from(
      new Set([...dismissedHosts, ...domains.map(({ host }) => host)])
    )
    writeDismissed(orgId, hosts)
    setDismissedHosts(hosts)
  }, [dismissedHosts, domains, orgId])

  const approve = useCallback(
    async (domain: PendingEmbedDomain) => {
      if (!orgId) {
        pushErrorToast("Unable to determine your organization for this URL.")
        return false
      }
      setApprovingHost(domain.host)
      try {
        // suggested_pattern comes from the API already in the form the matcher
        // needs (host + /*), so nobody has to hand-type the wildcard.
        await addApprovedUrl({
          org_id: orgId,
          domain_pattern: domain.suggested_pattern
        }).unwrap()
        setResolvedHosts(prev => [...prev, domain.host])
        pushSuccessToast(`${domain.host} approved`)
        // Approved URLs live in their own API slice, so approving cannot
        // invalidate this query by tag — refetch to confirm server-side.
        refetch()
        return true
      } catch {
        pushErrorToast(`Could not approve ${domain.host}. Please try again.`)
        return false
      } finally {
        setApprovingHost(null)
      }
    },
    [addApprovedUrl, orgId, pushErrorToast, pushSuccessToast, refetch]
  )

  // Both writes answer HTTP 200 with {status:"error"} when the server refuses
  // them (the same trap as POST /org/domains.json), so a resolved request is
  // not proof of anything — the payload has to be checked.
  const postDecline = useCallback(
    async (host: string, action: "decline" | "undecline") => {
      const result = await declineEmbedDomain({
        org_id: orgId,
        host,
        action
      }).unwrap()
      if ((result as { status?: string })?.status !== "success") {
        throw new Error(`Failed to ${action} ${host}`)
      }
    },
    [declineEmbedDomain, orgId]
  )

  const decline = useCallback(
    async (domain: PendingEmbedDomain) => {
      if (!orgId) {
        pushErrorToast("Unable to determine your organization for this URL.")
        return false
      }
      setDecliningHost(domain.host)
      try {
        await postDecline(domain.host, "decline")
        setResolvedHosts(prev => [...prev, domain.host])
        setRestoredHosts(prev => prev.filter(host => host !== domain.host))
        pushSuccessToast(`${domain.host} hidden. It is still blocked.`)
        refetch()
        return true
      } catch {
        pushErrorToast(`Could not hide ${domain.host}. Please try again.`)
        return false
      } finally {
        setDecliningHost(null)
      }
    },
    [orgId, postDecline, pushErrorToast, pushSuccessToast, refetch]
  )

  const restore = useCallback(
    async (domain: DeclinedEmbedDomain) => {
      if (!orgId) {
        pushErrorToast("Unable to determine your organization for this URL.")
        return false
      }
      setRestoringHost(domain.host)
      try {
        await postDecline(domain.host, "undecline")
        setRestoredHosts(prev => [...prev, domain.host])
        // Drop it from the session's resolved list too, or a domain declined
        // and restored without a page reload would stay hidden from both lists.
        setResolvedHosts(prev => prev.filter(host => host !== domain.host))
        pushSuccessToast(`${domain.host} restored`)
        refetch()
        return true
      } catch {
        pushErrorToast(`Could not restore ${domain.host}. Please try again.`)
        return false
      } finally {
        setRestoringHost(null)
      }
    },
    [orgId, postDecline, pushErrorToast, pushSuccessToast, refetch]
  )

  // The server total lags an approve or decline until the refetch lands, so
  // discount the hosts we resolved that are still in the payload. Counting only
  // those keeps it exact: once the refetch drops them, nothing is
  // double-subtracted.
  const resolvedStillInPayload = useMemo(
    () =>
      (data?.pending_domains ?? []).filter(({ host }) =>
        resolvedHosts.includes(host)
      ).length,
    [data, resolvedHosts]
  )

  return {
    domains,
    // A narrowed list has no server-side total to report.
    count: integrationId
      ? domains.length
      : Math.max(0, (data?.total ?? 0) - resolvedStillInPayload),
    undismissed,
    dismiss,
    approve,
    approvingHost,
    decline,
    decliningHost,
    declined,
    restore,
    restoringHost,
    isFetching
  }
}
